import React, { useEffect, useState, useCallback } from "react";
import { Table, Spin, Empty, message, Alert } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import Breadcrumbs from "../../../../../components/Breadcrumbs";
import AttendanceReportFilter from "./components/AttendanceReportFilter";
import { useLazyGetFirstInLastOutQuery } from "../../../../../services/attendance.service";
import { useLazyGetBranchesQuery, useLazyGetDepartmentsQuery } from "../../../../../services/settings.service";
import dayjs from "dayjs";
import ROLES from "../../../../../constants/roles";

const FirstInLastOutReport = () => {
  const user = useSelector((state) => state.user?.user || {});
  const userRole = user.role || "";
  const userBranchId = user?.branch?.id || null;
  const userDepartmentId = user?.employee.department?.id || null;

  const [data, setData] = useState([]);
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [dateRange, setDateRange] = useState([
    dayjs().startOf("month"),
    dayjs().endOf("month"),
  ]);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [hasSearched, setHasSearched] = useState(false);

  const [getFirstInLastOut, { isLoading }] = useLazyGetFirstInLastOutQuery();
  const [getBranches] = useLazyGetBranchesQuery();
  const [getDepartments] = useLazyGetDepartmentsQuery();

  // Set default branch/department for HOD and Manager roles
  useEffect(() => {
    if ([ROLES.HOD, ROLES.MANAGER].includes(userRole)) {
      if (userBranchId) setSelectedBranch(userBranchId);
      if (userDepartmentId && userRole === ROLES.HOD) setSelectedDepartment(userDepartmentId);
    }
  }, [userRole, userBranchId, userDepartmentId]);

  useEffect(() => {
    fetchBranches();
    fetchDepartments();
  }, []);

  const fetchBranches = async () => {
    try {
      const result = await getBranches({ page: 1, limit: 100 });
      if (result.data?.success) {
        setBranches(result.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const result = await getDepartments({ page: 1, limit: 100 });
      if (result.data?.success) {
        setDepartments(result.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching departments:", error);
    }
  };

  const fetchData = useCallback(async () => {
    // For HOD and Manager, branch_id is mandatory
    const branchToUse = [ROLES.HOD, ROLES.MANAGER].includes(userRole) ? (selectedBranch || userBranchId) : selectedBranch;
    const departmentToUse = userRole === ROLES.HOD ? (selectedDepartment || userDepartmentId) : selectedDepartment;

    const params = {
      start_date: dateRange[0].format("YYYY-MM-DD"),
      end_date: dateRange[1].format("YYYY-MM-DD"),
    };

    if (branchToUse) params.branch_id = branchToUse;
    if (departmentToUse) params.department_id = departmentToUse;

    try {
      const result = await getFirstInLastOut(params);

      if (result.data?.success) {
        setData(result.data.data || []);
        setHasSearched(true);
      } else {
        message.error("Failed to fetch attendance data");
      }
    } catch (error) {
      console.error("Error fetching attendance data:", error);
      message.error("Error fetching attendance data");
    }
  }, [dateRange, selectedBranch, selectedDepartment, userRole, userBranchId, userDepartmentId]);

  const handleSearch = () => {
    fetchData();
  };

  const handleExportPDF = () => {
    const branchToUse = [ROLES.HOD, ROLES.MANAGER].includes(userRole) ? (selectedBranch || userBranchId) : selectedBranch;
    const departmentToUse = userRole === ROLES.HOD ? (selectedDepartment || userDepartmentId) : selectedDepartment;

    window.open(
      `/api/v2/attendance/reports/first-in-last-out?start_date=${dateRange[0].format("YYYY-MM-DD")}&end_date=${dateRange[1].format("YYYY-MM-DD")}&format=pdf${branchToUse ? `&branch_id=${branchToUse}` : ""}${departmentToUse ? `&department_id=${departmentToUse}` : ""}`,
      "_blank"
    );
  };

  const handleExportExcel = () => {
    const branchToUse = [ROLES.HOD, ROLES.MANAGER].includes(userRole) ? (selectedBranch || userBranchId) : selectedBranch;
    const departmentToUse = userRole === ROLES.HOD ? (selectedDepartment || userDepartmentId) : selectedDepartment;

    window.open(
      `/api/v2/attendance/reports/first-in-last-out?start_date=${dateRange[0].format("YYYY-MM-DD")}&end_date=${dateRange[1].format("YYYY-MM-DD")}&format=excel${branchToUse ? `&branch_id=${branchToUse}` : ""}${departmentToUse ? `&department_id=${departmentToUse}` : ""}`,
      "_blank"
    );
  };

  const handlePrint = () => {
    window.print();
  };

  const columns = [
    {
      title: "Emp ID",
      dataIndex: "employee_number",
      key: "employee_number",
      width: 80,
      fixed: "left",
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 200,
      fixed: "left",
      render: (text, record) => (
        <span>{text || `${record.first_name || ""} ${record.last_name || ""}`}</span>
      ),
    },
    {
      title: "First Punch",
      dataIndex: "first_punch",
      key: "first_punch",
      width: 120,
      render: (time) => (time ? dayjs(time).format("hh:mm:ss A") : "-"),
    },
    {
      title: "Last Punch",
      dataIndex: "last_punch",
      key: "last_punch",
      width: 120,
      render: (time) => (time ? dayjs(time).format("HH:mm:ss") : "-"),
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: 120,
      render: (date) => (date ? dayjs(date).format("YYYY-MM-DD") : "-"),
    },
    {
      title: "Total Time",
      dataIndex: "total_hours",
      key: "total_hours",
      width: 100,
      render: (hours) => {
        if (!hours) return "0 hrs";
        if (typeof hours === "number") {
          const h = Math.floor(hours);
          return `${h} hrs`;
        }
        return hours;
      },
    },
  ];

  // Flatten data for table - group by department
  const flattenedData = [];
  data.forEach((dept, deptIndex) => {
    // Add department header row
    flattenedData.push({
      key: `dept-${deptIndex}`,
      isDepartmentHeader: true,
      department_name: dept.department_name || dept.department,
    });

    // Add records
    (dept.records || []).forEach((record, recordIndex) => {
      const empName = record.employee_name || `${record.first_name || ""} ${record.last_name || ""}`;
      if (employeeSearch && !empName.toLowerCase().includes(employeeSearch.toLowerCase())) {
        return;
      }

      flattenedData.push({
        key: `record-${deptIndex}-${recordIndex}`,
        ...record,
      });
    });
  });

  const getReportTitle = () => {
    return "First In Last Out Attendance Report";
  };

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <Breadcrumbs
          maintitle={getReportTitle()}
          title="Attendance Report"
          subtitle="First In Last Out"
        />

        <AttendanceReportFilter
          branches={branches}
          departments={departments}
          selectedBranch={selectedBranch}
          selectedDepartment={selectedDepartment}
          dateRange={dateRange}
          employeeSearch={employeeSearch}
          onBranchChange={setSelectedBranch}
          onDepartmentChange={setSelectedDepartment}
          onDateRangeChange={setDateRange}
          onEmployeeSearchChange={setEmployeeSearch}
          onSearch={handleSearch}
          onExportPDF={handleExportPDF}
          onExportExcel={handleExportExcel}
          onPrint={handlePrint}
          isLoading={isLoading}
          showEmployeeSearch={true}
          userRole={userRole}
          hasSearched={hasSearched}
        />

        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                {isLoading ? (
                  <div className="text-center py-5">
                    <Spin size="large" />
                  </div>
                ) : !hasSearched ? (
                  <Alert
                    message="Select filters and click SEARCH to display the report"
                    type="info"
                    showIcon
                    icon={<InfoCircleOutlined />}
                    style={{ maxWidth: 500, margin: "50px auto" }}
                  />
                ) : flattenedData.length === 0 ? (
                  <Empty description="No attendance data found" />
                ) : (
                  <Table
                    columns={columns}
                    dataSource={flattenedData}
                    pagination={false}
                    scroll={{ x: "max-content" }}
                    size="small"
                    bordered
                    rowClassName={(record) =>
                      record.isDepartmentHeader ? "department-header-row" : ""
                    }
                    components={{
                      body: {
                        row: ({ children, ...restProps }) => {
                          const record = flattenedData.find(
                            (d) => d.key === restProps["data-row-key"]
                          );
                          if (record?.isDepartmentHeader) {
                            return (
                              <tr {...restProps}>
                                <td
                                  colSpan={columns.length}
                                  style={{
                                    backgroundColor: "#e8e8e8",
                                    fontWeight: "bold",
                                    padding: "8px 12px",
                                  }}
                                >
                                  {record.department_name}
                                </td>
                              </tr>
                            );
                          }
                          return <tr {...restProps}>{children}</tr>;
                        },
                      },
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FirstInLastOutReport;
